import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  Drawer,
  Spin,
} from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import { TaskHelpers } from 'ontraccr-common';

import Permissions from '../../../auth/Permissions';
import TimeTracking from '../../../state/timeTracking';
import useUpdateCostcodeIdMap from '../../../costcodes/useUpdateCostcodeIdMap';

import BorderlessButton from '../../../common/buttons/BorderlessButton';
import DrawerSubmitFooter from '../../../common/containers/DrawerSubmitFooter';
import DisplayText from '../../../common/text/DisplayText';
import WorkSlot from '../../../timecards/TimeCard/WorkSlot';

const checkIcon = <CheckOutlined style={{ marginLeft : 0, color: 'forestgreen', fontSize: 20 }}/>;

const isSelectableTimeCard = (task) => {
  if (
    task.type === 'break'
    || (
      !task.startTime
      && !task.otStartTime
      && !task.doubleOTStartTime
      && !task.ptoStartTime
    )
  ) return false;
  const endTime = TaskHelpers.getEndTime(task);
  if (!endTime) return false;
  return DateTime.fromMillis(endTime) >= DateTime.local().startOf('day').minus({ days: 7 });
};

export default function LabourHoursDrawer({
  visible,
  onClose,
  onSubmit: propSubmit,
}) {
  const dispatch = useDispatch();
  const timeEntryUserMap = useSelector((state) => state.timeTracking.timeEntryUserMap);
  const tasks = useMemo(() => (
    (timeEntryUserMap[Permissions.id] ?? []).filter(isSelectableTimeCard)
  ), [timeEntryUserMap]);

  useUpdateCostcodeIdMap({
    items: visible ? tasks : [],
  });

  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const selectedSet = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);

  const toggleSelected = useCallback((selectedTask) => () => {
    const newSelected = [...selected];
    if (selectedSet.has(selectedTask.id)) {
      setSelected(newSelected.filter((s) => s.id !== selectedTask.id));
    } else {
      setSelected(newSelected.concat([selectedTask]));
    }
  }, [selected, selectedSet]);

  const onSubmit = useCallback(() => {
    propSubmit(selected);
  }, [selected, propSubmit]);

  useEffect(() => {
    setSelected([]);
    if (!visible) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const fetchTasks = async () => {
      setLoading(true);
      const now = DateTime.local();
      await dispatch(TimeTracking.getTimeEntries({
        userId: Permissions.id,
        startTime: now.startOf('day').minus({ days: 7 }).toMillis(),
        endTime: now.endOf('day').toMillis(),
        getRunningTasks: false,
        getCardDetails: false,
      }));
      if (cancelled) return;
      setLoading(false);
    };

    fetchTasks();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  return (
    <Drawer
      visible={visible}
      title="Add Labour Hours"
      width={400}
      onClose={onClose}
    >
      <div id="labour-add-drawer-scroll">
        {loading && (
          <Row justify="center" align="middle" style={{ height: '100%' }}>
            <Spin />
          </Row>
        )}
        {!loading && tasks.length === 0 && (
          <Row justify="center" align="middle" style={{ height: '100%', padding: 24 }}>
            <DisplayText title="No time cards in the last 7 days" style={{ marginBottom: 0 }} />
          </Row>
        )}
        {!loading && tasks.map((task) => (
          <Row key={task.id} justify="space-around" className="labour-add-task-row">
            <Col flex="30px">
              {selectedSet.has(task.id) && (
                <Row justify="center" align="middle" style={{ height: '100%' }}>
                  <BorderlessButton
                    iconNode={checkIcon}
                    style={{ width: 30 }}
                    onClick={toggleSelected(task)}
                  />
                </Row>
              )}
            </Col>
            <Col>
              <div
                className="labour-add-drawer-task"
                onClick={toggleSelected(task)}
              >
                <WorkSlot task={task} />
              </div>
            </Col>
            <Col flex="30px" />
          </Row>
        ))}
      </div>
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmit}
        loading={loading}
      />
    </Drawer>
  );
}
